<!-- Copyright (c) 2020-2023 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<template>
  <ValidationObserver
    v-slot="{ invalid }"
    ref="observer">
    <BModal
      id="userDetailsModal"
      cancel-variant="outline-secondary"
      size="lg"
      title-class="h5"
      title-tag="h2"
      :title="title"
      @show="setModal"
      @keydown.enter.native.prevent="saveForm"
      :static="isTesting">
      <!-- Editing profile currently only supports String, Number and Boolean-->
      <BContainer>
        <BRow>
          <template v-if="formFields.length">
            <BFormGroup
              class="w-100 mb-0"
              :label="title"
              label-sr-only>
              <template v-for="(field, index) in formFields">
                <div
                  :key="index">
                  <FrField
                    v-model="field.value"
                    class="personal-info-field"
                    :label="field.title"
                    :name="field.name"
                    :type="field.format ? field.format : field.type"
                    :validation="field.validation"
                    :testid="`edit-personal-info-${index}`"
                    v-if="(field.type === 'string' || field.type === 'number' || field.type === 'boolean') && field.name !== 'stateProvince' && field.name !== 'addressId'" />
                  <FrListField
                    v-else-if="field.type === 'array' && field.name !== 'privileges'"
                    v-model="field.value"
                    class="w-100 personal-info-field"
                    :description="field.description"
                    :items="field.items"
                    :label="field.title"
                    :name="field.name"
                    :required="field.required"
                    :index="index"
                    v-on="$listeners"
                    @input="updateField(index, $event)" />
                </div>
              </template>
            </BFormGroup>
            <template v-for="(field, index) in formFields" :key="index">
              <BFormGroup
                v-if="field.name === 'stateProvince'"
                class="w-100 mb-0">
                <template>
                  <template slot="label">State/Province</template>
                  <ValidationProvider rules="required" v-slot="validationContext">
                    <BFormSelect
                      v-model="field.value"
                      class="w-100 mb-3"
                      :label="field.title"
                      :name="field.name"
                      :options="stateDictionary"
                      :testid="`edit-personal-info-${index}`"
                      :description="field.description"
                      @change="updateAddressList()"
                      :state="getValidationState(validationContext)">
                      <template #first>
                        <BFormSelectOption
                          disabled
                          :value=null>
                          -- Please select a state --
                        </BFormSelectOption>
                      </template>
                    </BFormSelect>
                    <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                  </ValidationProvider>
                </template>
              </BFormGroup>
            </template>
            <template v-for="(field, index) in formFields" :key="index">
              <BFormGroup
                v-if="field.name === 'addressId'"
                class="w-100 mb-0">
                <template>
                  <template slot="label">Address</template>
                  <ValidationProvider rules="required" v-slot="validationContext">
                    <BFormSelect
                      v-model="field.value"
                      class="w-100 mb-3"
                      :label="field.title"
                      :name="field.name"
                      :options="addressList"
                      :testid="`edit-personal-info-${index}`"
                      :description="field.description"
                      :state="getValidationState(validationContext)"
                      @change="updateField(index, $event); $nextTick(() => { $refs.observer.setErrors({ addressId: [] }); $refs.observer.validate(); })">
                      <template #first>
                        <BFormSelectOption
                          disabled
                          :value=null>
                          -- Please select an address --
                        </BFormSelectOption>
                      </template>
                    </BFormSelect>
                    <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                  </ValidationProvider>
                </template>
              </BFormGroup>
            </template>
            <hr class="w-100"/>
            <template>
              <div id="ensMessage">
                <div class="col-sm-12 subtitle text-muted">
                  <p class="mb-0">Visit the <a href="https://nsd.rdc.noaa.gov/login?ReturnUrl=%2fmember%2fcontact" target="_blank">NOAA Staff Directory</a> to update your personal contact information for the Emergency Notification System (see the <a href="https://drive.google.com/u/0/open?id=1k71G8ko-pIlLVzRD70kAkDcp04-kEb-e" target="_blank">ENS Help Guide</a>).</p>
                </div>
              </div>
            </template>
          </template>
          <h1
            v-else
            class="text-center h3"
            data-testid="edit-personal-info-no-fields">
            {{ $t('pages.profile.editProfile.noFields') }}
          </h1>
        </BRow>
      </BContainer>
      <template #modal-footer="{ cancel }">
        <BButton
          variant="link mr-2"
          @click="cancel()"
          data-testid="btn-edit-personal-info-cancel">
          {{ $t('common.cancel') }}
        </BButton>
        <BButton
          variant="primary"
          :disabled="internalUser || invalid"
          @click="saveForm"
          data-testid="btn-edit-personal-info-save">
          {{ $t('common.save') }}
        </BButton>
      </template>
    </BModal>
  </ValidationObserver>
</template>

<script>
import {
  cloneDeep,
  each,
  filter,
  map,
  reject,
} from 'lodash';
import {
  BButton,
  BContainer,
  BFormGroup,
  BFormSelect,
  BModal,
  BRow,
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { mapState } from 'vuex';
import ResourceMixin from '@forgerock/platform-shared/src/mixins/ResourceMixin';
import RestMixin from '@forgerock/platform-shared/src/mixins/RestMixin';
import NotificationMixin from '@forgerock/platform-shared/src/mixins/NotificationMixin';
import FrField from '@forgerock/platform-shared/src/components/Field';
import FrListField from '@forgerock/platform-shared/src/components/ListField';
import ListsMixin from '@forgerock/platform-shared/src/mixins/ListsMixin';

/**
 * @description Displays a users profile, auto generates fields based off of resource schema. Currently only displays strings, numbers and booleans. In the case of a policy
 * save error it will highlight the appropriate field and display a policy error. For custom profile changes (e.g. adding a dropdown) this would be the primary file to add these
 * adjustments.
 */
export default {
  name: 'EditPersonalInfo',
  mixins: [
    ListsMixin,
    NotificationMixin,
    ResourceMixin,
    RestMixin,
  ],
  components: {
    BButton,
    BContainer,
    BFormGroup,
    BFormSelect,
    BModal,
    BRow,
    FrField,
    FrListField,
    ValidationObserver,
    ValidationProvider,
  },
  computed: {
    ...mapState({
      userId: (state) => state.UserStore.userId,
      managedResource: (state) => state.UserStore.managedResource,
      internalUser: (state) => state.UserStore.internalUser,
    }),
  },
  props: {
    /**
     * Schema data for profile
     */
    schema: {
      type: Object,
      required: true,
    },
    /**
     * Profile data
     */
    profile: {
      type: Object,
      required: true,
    },
    /**
     * Should only be used to mount this modal in isolation for testing
     */
    isTesting: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      formFields: [],
      originalFormFields: [],
      stateDictionary: [],
      addressList: [],
      title: this.$t('pages.profile.editProfile.userDetailsTitle'),
    };
  },
  methods: {
    /**
     * Get form fields from schema and profile data
     *
     * @returns {Object} profile fields
     */
    generateFormFields() {
      const { order, properties, required } = this.schema;
      const filteredOrder = filter(order, (propName) => (properties[propName].userEditable
                            && properties[propName].viewable
                            && properties[propName].scope !== 'private'
                            && properties[propName].type !== 'object')
                            || propName === 'addressId'
                            || propName === 'stateProvince');
      const formFields = map(filteredOrder, (name) => ({
        name,
        title: `${properties[name].title} ${required.includes(name) ? '' : this.$t('common.optional')}`.trim(),
        value: this.profile[name] || null,
        type: properties[name].type,
        description: properties[name].description,
        items: properties[name].items,
        format: properties[name].format,
        validation: required.includes(name) ? 'required' : '',
      }));

      return formFields;
    },
    /**
     * Generate state dictionary
     */
    async fetchStateData() {
      try {
        const idmInstance = this.getRequestService();
        const result = await idmInstance.get(`managed/address/?_queryFilter=st+sw+''&_fields=st,stateName&_sortKeys=stateName`);

        const stateData = result.data.result;
        this.stateDictionary = stateData.reduce((dictionary, state) => {
          dictionary[state.st] = state.stateName;
          return dictionary;
        }, {});
      } catch (error) {
        console.error(error);
      }
    },
    /**
     * Build address list for users current state
     */
    async setAddressList() {
      const idmInstance = this.getRequestService();
      const formFields = this.formFields;
      const targetName = 'stateProvince';

      function findStateByName(array, targetName) {
        const result = array.find(item => item.name === targetName);
        return result ? result.value : null;
      }

      const state = findStateByName(formFields, targetName);

      if (state !== null) {
        try {
          const result = await idmInstance.get(`managed/address/?_queryFilter=st+eq+"${state}"&_sortKeys=street`);
          const addressData = result.data.result;

          this.addressList = addressData.map(address => {
            let builtText = `${address.street}, `;
            if (address.buildingName) {
              builtText += `Bldg: ${address.buildingName}, `;
            }
            builtText += `${address.city}, ${address.postalCode}`;

            return { value: address._id, text: builtText };
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        return null;
      }
    },
    /**
     * Update address list on state change
     */
    async updateAddressList() {
      const idmInstance = this.getRequestService();
      const formFields = this.formFields;
      
      // 1. Clear the current address value dynamically
      const addressFieldIndex = this.formFields.findIndex(item => item.name === 'addressId');
      if (addressFieldIndex !== -1) {
        this.formFields[addressFieldIndex].value = null;
      }

      const targetName = 'stateProvince';
      const state = formFields.find(item => item.name === targetName)?.value || null;

      if (state !== null) {
        try {
          // Await the API call
          const result = await idmInstance.get(`managed/address/?_queryFilter=st+eq+"${state}"&_sortKeys=street`);
          const addressData = result.data.result;

          this.addressList = addressData.map(address => {
            let builtText = `${address.street}, `;
            if (address.buildingName) {
              builtText += `Bldg: ${address.buildingName}, `;
            }
            builtText += `${address.city}, ${address.postalCode}`;

            return { value: address._id, text: builtText };
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        this.addressList = [];
      }

      // 2. Force Vee-Validate to re-evaluate AFTER the async update completes
      this.$nextTick(async () => {
        if (this.$refs.observer) {
          this.$refs.observer.setErrors({ stateProvince: [] });
          
          // Awaiting the validation forces 'validated: true' on all providers.
          // This updates the 'invalid' flag for the Save button AND triggers
          // your getValidationState() to return false (red border) for the null address.
          await this.$refs.observer.validate();
        }
      });
    },
    getValidationState({
      dirty,
      validated,
      valid = null
    }) {
      return dirty || validated ? (valid ? null : valid) : null;
    },
    /**
     * Hide the modal
     */
    hideModal() {
      this.$root.$emit('bv::hide::modal', 'userDetailsModal');
    },
    /**
     * Set the form fields for the modal. Maintain the original values for patch
     */
    setModal() {
      this.fetchStateData();
      const formFields = this.generateFormFields();
      this.formFields = formFields;
      this.originalFormFields = cloneDeep(formFields);
      this.setAddressList();
    },
    /**
     * Save the updated profile information via patch.
     * Values are validated by backend before saving.
     */
    async saveForm() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        const idmInstance = this.getRequestService();
        const policyFields = {};

        each(this.formFields, (field) => {
          if (field.value !== null) {
            // Translate the UI's addressId into the backend's addressRef for validation
            if (field.name === 'addressId') {
              policyFields['addressRef'] = { _ref: `managed/address/${field.value}` };
            } else if (field.name !== 'stateProvince') {
              policyFields[field.name] = field.value;
            }
          }
        });

        idmInstance.post(`policy/${this.managedResource}/${this.userId}?_action=validateObject`, policyFields)
          .then((policyResult) => {
            // reject any failedPolicyRequirements on properties that don't exist in this.formFields
            policyResult.data.failedPolicyRequirements = reject(policyResult.data.failedPolicyRequirements, (policy) => !map(this.formFields, 'name').includes(policy.property));

            if (policyResult.data.failedPolicyRequirements.length === 0) {

              // Generate the standard patch payload array
              let patchPayload = this.generateUpdatePatch(this.originalFormFields, this.formFields);

              // Convert the virtual 'addressId' update into a physical 'addressRef' relationship update
              const addressPatchIndex = patchPayload.findIndex(patch => patch.field === '/addressId');

              if (addressPatchIndex !== -1) {
                const addressAction = patchPayload[addressPatchIndex];

                // Because address is strictly required, we can safely overwrite with a strict 'replace'
                patchPayload[addressPatchIndex] = {
                  operation: 'replace',
                  field: '/addressRef',
                  value: { _ref: `managed/address/${addressAction.value}` }
                };
              }

              // Strip stateProvince out of the patch payload since it is not userEditable
              patchPayload = patchPayload.filter(patch => patch.field !== '/stateProvince');

              // Emit the safely modified payload
              this.$emit('updateProfile', patchPayload);

              this.$refs.observer.reset();
              this.hideModal();
              // this.resetUserData();
            } else {
              const generatedErrors = this.findPolicyError({
                data: {
                  detail: {
                    failedPolicyRequirements: policyResult.data.failedPolicyRequirements,
                  },
                },
              }, this.formFields);

              this.$refs.observer.reset();
              if (generatedErrors.length > 0) {
                each(generatedErrors, (generatedError) => {
                  if (generatedError.exists) {
                    this.$refs.observer.setErrors({
                      [generatedError.field]: [generatedError.msg],
                    });
                  }
                });
              } else {
                this.displayNotification('error', this.$t('pages.profile.failedProfileSave'));
              }
            }
          })
          .catch((error) => {
            // Ensure silent failures are caught and logged to the browser console
            console.error('IDM Policy Validation Error:', error.response || error);
            
            // Optionally, show a generic UI toast notification so the user isn't stuck waiting
            this.displayNotification('error', 'An error occurred while validating profile changes. Check the console for details.');
          });
      }
    },
    /**
     * Update form data model with a new value. Used for editing a list field
     *
     * @param {Number} index index of form field
     * @param {Array} newValue new value of form field
     */
    updateField(index, newValue) {
      this.formFields[index].value = newValue;
      this.$forceUpdate();
    },
  },
};
</script>
<style lang="scss" scoped>

.personal-info-field {
  margin-bottom: 1.875rem
}

</style>
